import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  input,
  output,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import { AceActionHandler } from '../classes/ace-action-handler';

import { NgAceSidebarStateService } from '../services/sidebar-state.service';

import AceSidebar from '@ace/sidebar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[NgAceSidebar]',
  exportAs: 'NgAceSidebar',
})
export class NgAceSidebar extends AceActionHandler implements OnInit, OnChanges, AfterViewInit {
  private readonly el = inject(ElementRef);
  private readonly _ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly sidebarStateService = inject(NgAceSidebarStateService);
  protected injector: Injector;

  sidebar: AceSidebar | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly NgAceSidebar = input<any>(); // the options passed to Directive
  @Input() autoOpen!: boolean; // open the 'active' elements (submenus) by default on init
  @Input() autoRestore!: boolean; // restore scroll position and don't always 'open' the active item, for example if we are back from another layout (login), open the login's parent submenu
  @Input() autoHide!: boolean; // hide sidebar in mobile view when we click on a new route

  @Input() collapsed = false;
  @Output() collapsedChange = new EventEmitter<boolean>();
  readonly sidebarCollapsed = output();
  readonly sidebarExpanded = output();

  @Input() hidden = true;
  @Output() hiddenChange = new EventEmitter<boolean>();
  readonly sidebarShown = output();
  readonly sidebarHidden = output();

  constructor() {
    const injector = inject(Injector);

    super(injector, 1);
    this.injector = injector;

    ///////////////

    this.element = this.el.nativeElement;

    this.prevented = {
      collapse: false,
      expand: false,
      hide: false,
      show: false,
    };

    this.collapsed = false;
    this.hidden = true;
  }

  //////*********//////
  // sidebar desktop events
  @HostListener('collapse.ace.sidebar', ['$event'])
  onCollapseCallback(event: Event) {
    if (this.isPrevented('collapse')) event.preventDefault();
    this.collapsedChange.emit(true);
  }

  @HostListener('expand.ace.sidebar', ['$event'])
  onExpandCallback(event: Event) {
    if (this.isPrevented('expand')) event.preventDefault();
    this.collapsedChange.emit(false);
  }

  @HostListener('collapsed.ace.sidebar')
  onCollapsedCallback() {
    this.sidebarCollapsed.emit();
  }

  @HostListener('expanded.ace.sidebar')
  onExpandedCallback() {
    this.sidebarExpanded.emit();
  }

  //////*********//////
  // sidebar mobile events
  @HostListener('hide.ace.sidebar', ['$event'])
  onHideCallback(event: Event) {
    if (this.isPrevented('hide')) event.preventDefault();
    this.hiddenChange.emit(true);
  }

  @HostListener('show.ace.sidebar', ['$event'])
  onShowCallback(event: Event) {
    if (this.isPrevented('show')) event.preventDefault();
    this.hiddenChange.emit(false);
  }

  @HostListener('hidden.ace.sidebar')
  onHiddenCallback() {
    this.sidebarHidden.emit();
  }

  @HostListener('shown.ace.sidebar')
  onShownCallback() {
    this.sidebarShown.emit();
  }

  ///////////

  ngOnInit(): void {
    super.init();
    ////////////

    this.autoOpen = typeof this.autoOpen !== 'undefined' ? this.autoOpen : true;
    this.autoRestore = typeof this.autoRestore !== 'undefined' ? this.autoRestore : true;
    this.autoHide = typeof this.autoHide !== 'undefined' ? this.autoHide : true;

    this._ngZone.runOutsideAngular(() => {
      const options = this.NgAceSidebar() || {};
      options.subtoggle = false;
      this.sidebar = AceSidebar.getInstance(this.element, options);
    });

    this.collapsed = this.sidebar?.isCollapsed() ?? false;
    this.hidden = this.sidebar?.isHidden() ?? true;

    this.collapsedChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: boolean) => {
      this.collapsed = value;
    });

    this.hiddenChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: boolean) => {
      this.hidden = value;
    });

    ///
    if (this.autoOpen && this.element.id)
      this.sidebarStateService.setOpenOnLoad(this.element.id, this.router.url, this.autoRestore);

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      if (val instanceof NavigationEnd) {
        // if mobile sidebar is visible, on navigation change hide it
        if (this.autoHide && !this.sidebar?.isHidden()) {
          this.sidebar?.hide();
        }
      } else if (val instanceof NavigationStart) {
        if (this.autoRestore) {
          // we want to end up at the same sidebar scroll position
          // useful for when we navigate away from this layout to another, and then we come back
          const scroller = this.element.querySelector('[class*="ace-scroll"]');
          if (scroller && this.element.id) {
            this.sidebarStateService.set(this.element.id, 'scroll', scroller.scrollTop);
          }
        }
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this.autoRestore) return;
    const scroller = this.element.querySelector('[class*="ace-scroll"]');
    if (scroller && this.element.id) {
      const scroll = this.sidebarStateService.get(this.element.id, 'scroll');
      if (scroll) scroller.scrollTop = scroll as number;
    }
  }

  // respond to changes to 'collapsed' or 'hidden'
  ngOnChanges(changes: SimpleChanges) {
    if (!this.sidebar) return;

    if (changes.collapsed) {
      if (changes.collapsed.currentValue === true) {
        this.collapse();
      } else {
        this.expand();
      }
    } else if (changes.hidden) {
      if (changes.hidden.currentValue === true) {
        this.hide();
      } else {
        this.show();
      }
    }
  }

  // AceSidebar functions
  toggle(): void {
    if (this.sidebar?.isCollapsed()) {
      this.expand();
    } else {
      this.collapse();
    }
  }
  collapse(): void {
    if (this.isPrevented('collapse')) return;
    this._ngZone.runOutsideAngular(() => this.sidebar?.collapse());
    this.collapsed = this.sidebar?.isCollapsed() ?? false;
  }
  expand(): void {
    if (this.isPrevented('expand')) return;
    this._ngZone.runOutsideAngular(() => this.sidebar?.expand());
    this.collapsed = this.sidebar?.isCollapsed() ?? false;
  }

  toggleMobile(): void {
    if (this.sidebar?.isHidden()) {
      this.show();
    } else {
      this.hide();
    }
  }
  hide(): void {
    if (this.isPrevented('hide')) return;
    this._ngZone.runOutsideAngular(() => this.sidebar?.hide());
    this.hidden = this.sidebar?.isHidden() ?? true;
  }
  show(): void {
    if (this.isPrevented('show')) return;
    this._ngZone.runOutsideAngular(() => this.sidebar?.show());
    this.hidden = this.sidebar?.isHidden() ?? false;
  }
}
